import React, { useEffect, useMemo, useState } from "react";
import { DndContext, PointerSensor, KeyboardSensor, closestCenter, useSensor, useSensors } from "@dnd-kit/core";
import { sortableKeyboardCoordinates } from "@dnd-kit/sortable";
import { ArrowLeft, History, Loader2 } from "lucide-react";
import { Button } from "../../../../components/ui/button";
import { Input } from "../../../../components/ui/input";
import StatusBadge from "../../components/StatusBadge";
import MutationErrorAlert from "../../components/MutationErrorAlert";
import EmailBuilderAssetLibrary from "./EmailBuilderAssetLibrary";
import EmailBuilderCanvas from "./EmailBuilderCanvas";
import EmailBuilderProperties from "./EmailBuilderProperties";
import { ModeToggle, DeviceToggle } from "./EmailBuilderToggles";
import {
  normalizeVersionToDraft,
  addBlockFromAsset,
  removeBlock,
  updateBlockRef,
  updateFixedSlot,
  updateImagePresentation,
  addCtaEcho,
  validateDraftForSave,
} from "./builderDraft";
import { resolveDragEnd } from "./builderDnd";
import { saveDraftToStorage, loadDraftFromStorage, clearDraftFromStorage } from "./draftRecovery";

/**
 * CreativeEmailBuilder — EMAILS-IMPL-015
 *
 * The governed Email Builder: LEFT Asset library / CENTER Email Canvas
 * (fixed metadata + ordered body blocks) / RIGHT Properties, per
 * CRE-SPEC-001's Version 4.2.0 Canvas Layout amendment. A single
 * `DndContext` spans the library AND the Canvas -- true cross-panel
 * drag/drop, not the Funnel Builder's single-list reorder-only pattern.
 *
 * `fingerprint` and `canvasLayout` are held as ONE synchronized draft
 * object (builderDraft.js) so they can never drift apart in Builder
 * state (§25). Nothing here is persisted until an explicit Save New
 * Version -- Creative Versions remain immutable; this is local,
 * non-authoritative editing state only (§36).
 */
export default function CreativeEmailBuilder({
  creativeRow,
  historicalEntry,
  onClose,
  onSave,
  saving,
  saveError,
  onClearSaveError,
  onViewHistory,
}) {
  const isHistoricalSource = Boolean(historicalEntry);
  const sourceVersion = historicalEntry || creativeRow;
  const sourceVersionString = historicalEntry ? historicalEntry.version : creativeRow.current_version;

  const [draft, setDraft] = useState(() => normalizeVersionToDraft(sourceVersion));
  const [baseVersion] = useState(sourceVersionString);
  const [readOnlyHistorical, setReadOnlyHistorical] = useState(isHistoricalSource);
  const [dirty, setDirty] = useState(false);
  const [selectedBlockId, setSelectedBlockId] = useState(null);
  const [mode, setMode] = useState("builder");
  const [device, setDevice] = useState("desktop");
  const [versionInput, setVersionInput] = useState("");
  const [clientError, setClientError] = useState(null);
  const [recoverableDraft, setRecoverableDraft] = useState(null);

  // Refresh/accidental-loss protection (§37) -- non-authoritative, only
  // ever offered against the SAME starting Version this draft began from
  // (the storage key itself is scoped by creativeId+baseVersion, so a
  // draft saved against an older Version can never be silently offered
  // once the server's current Version has moved on).
  useEffect(() => {
    if (isHistoricalSource) return;
    const recovered = loadDraftFromStorage(creativeRow.creative_id, baseVersion);
    if (recovered) setRecoverableDraft(recovered);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (isHistoricalSource || readOnlyHistorical || !dirty) return;
    saveDraftToStorage(creativeRow.creative_id, baseVersion, draft);
  }, [draft, dirty, isHistoricalSource, readOnlyHistorical, creativeRow.creative_id, baseVersion]);

  const readOnly = mode === "preview" || readOnlyHistorical;

  const applyMutation = (updater) => {
    if (readOnlyHistorical) return;
    setDraft((prev) => updater(prev));
    setDirty(true);
    setClientError(null);
    onClearSaveError?.();
  };

  const presentFamilies = useMemo(() => new Set(draft.canvasLayout.filter((b) => b.role !== "echo").map((b) => b.family)), [draft.canvasLayout]);
  const hasCtaEcho = draft.canvasLayout.some((b) => b.family === "CTA" && b.role === "echo");

  const handleQuickAdd = (key, ref) => applyMutation((prev) => addBlockFromAsset(prev, { key, ...ref }, prev.canvasLayout.length));
  const handleRemoveBlock = (blockId) => {
    applyMutation((prev) => removeBlock(prev, blockId));
    if (selectedBlockId === blockId) setSelectedBlockId(null);
  };
  const handleChangeRef = (blockId, ref) => applyMutation((prev) => updateBlockRef(prev, blockId, ref));
  const handleChangeFixedSlot = (key, ref) => applyMutation((prev) => updateFixedSlot(prev, key, ref));
  const handleChangeImagePresentation = (patch) => applyMutation((prev) => updateImagePresentation(prev, patch));
  const handleAddCtaEcho = () => applyMutation((prev) => addCtaEcho(prev));

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 1 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const handleDragEnd = (event) => {
    if (readOnly) return;
    const next = resolveDragEnd(draft, event);
    if (next !== draft) {
      setDraft(next);
      setDirty(true);
      setClientError(null);
      onClearSaveError?.();
    }
  };

  const handleUseAsStartingPoint = () => {
    setReadOnlyHistorical(false);
    setDirty(true);
    setMode("builder");
  };

  const handleRestoreDraft = () => {
    if (recoverableDraft) {
      setDraft(recoverableDraft);
      setDirty(true);
    }
    setRecoverableDraft(null);
  };
  const handleDiscardRecoverable = () => {
    clearDraftFromStorage(creativeRow.creative_id, baseVersion);
    setRecoverableDraft(null);
  };

  const handleSave = async () => {
    setClientError(null);
    if (!versionInput.trim()) {
      setClientError("A new Version string is required (e.g. 2.0.0).");
      return;
    }
    const validation = validateDraftForSave(draft);
    if (!validation.valid) {
      setClientError(validation.errors.join(" "));
      return;
    }
    try {
      await onSave(creativeRow.creative_id, draft, versionInput.trim());
      clearDraftFromStorage(creativeRow.creative_id, baseVersion);
      setDirty(false);
      setReadOnlyHistorical(false);
      setVersionInput("");
    } catch {
      // saveError already surfaced via the parent's mutationError
    }
  };

  return (
    <div className="space-y-3" data-testid="creative-email-builder">
      <div className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-white/10 bg-white/5 p-3" data-testid="builder-topbar">
        <div className="flex items-center gap-3">
          <Button type="button" size="sm" variant="ghost" className="gap-1.5 text-slate-300" onClick={onClose} data-testid="builder-close-button">
            <ArrowLeft className="h-4 w-4" /> Back to Creatives
          </Button>
          <div className="h-6 w-px bg-white/10" />
          <div>
            <div className="flex items-center gap-2 text-sm">
              <span className="font-medium text-slate-100">{creativeRow.name}</span>
              <span className="font-mono text-xs text-slate-500" data-testid="builder-creative-id">{creativeRow.creative_id}</span>
              <StatusBadge status={creativeRow.status} testId="builder-creative-status" />
            </div>
            <div className="text-xs text-slate-500" data-testid="builder-current-version">
              Current Version {creativeRow.current_version || "—"}
              {isHistoricalSource && (
                <span className="ml-2 text-amber-300" data-testid="builder-viewing-historical">
                  Viewing historical Version {historicalEntry.version} (read-only)
                </span>
              )}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <ModeToggle mode={mode} onChange={setMode} />
          <DeviceToggle device={device} onChange={setDevice} />
          {onViewHistory && (
            <Button type="button" size="sm" variant="outline" className="gap-1.5 border-white/10 text-slate-200" onClick={onViewHistory} data-testid="builder-history-button">
              <History className="h-3.5 w-3.5" /> History
            </Button>
          )}
        </div>
      </div>

      {readOnlyHistorical && (
        <div className="flex items-center justify-between rounded-lg border border-amber-400/30 bg-amber-500/10 p-3 text-sm text-amber-200" data-testid="builder-historical-banner">
          <span>This is a read-only view of a historical Version. It cannot be edited or saved.</span>
          <Button type="button" size="sm" className="bg-violet-600 text-white hover:bg-violet-500" onClick={handleUseAsStartingPoint} data-testid="builder-use-as-starting-point">
            Use as Starting Point for New Version
          </Button>
        </div>
      )}

      {recoverableDraft && !readOnlyHistorical && (
        <div className="flex items-center justify-between rounded-lg border border-violet-400/30 bg-violet-500/10 p-3 text-sm text-violet-200" data-testid="builder-recovery-banner">
          <span>An unsaved draft from a previous session was found for this Creative.</span>
          <div className="flex gap-2">
            <Button type="button" size="sm" variant="outline" className="border-white/10 text-slate-200" onClick={handleDiscardRecoverable} data-testid="builder-discard-recovery">
              Discard
            </Button>
            <Button type="button" size="sm" className="bg-violet-600 text-white hover:bg-violet-500" onClick={handleRestoreDraft} data-testid="builder-restore-recovery">
              Restore Draft
            </Button>
          </div>
        </div>
      )}

      {!readOnlyHistorical && (
        <div className="flex flex-wrap items-center gap-3 rounded-lg border border-white/10 bg-white/5 p-3" data-testid="builder-save-bar">
          <Input
            value={versionInput}
            onChange={(e) => setVersionInput(e.target.value)}
            placeholder="New Version, e.g. 2.0.0"
            className="h-8 w-48 text-sm"
            data-testid="builder-version-input"
          />
          <Button type="button" size="sm" className="gap-1.5 bg-violet-600 text-white hover:bg-violet-500" disabled={saving} onClick={handleSave} data-testid="builder-save-button">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : null} Save New Version
          </Button>
          {dirty && <span className="text-xs text-slate-500" data-testid="builder-dirty-indicator">Unsaved changes</span>}
          <MutationErrorAlert error={saveError || (clientError ? { message: clientError } : null)} testId="builder-save-error" />
        </div>
      )}

      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
        <div className="grid grid-cols-1 gap-3 lg:grid-cols-[300px_1fr_320px]" style={{ minHeight: "560px" }}>
          <EmailBuilderAssetLibrary presentFamilies={presentFamilies} onQuickAdd={handleQuickAdd} disabled={readOnly} />
          <EmailBuilderCanvas
            draft={draft}
            selectedBlockId={selectedBlockId}
            onSelectBlock={setSelectedBlockId}
            onRemoveBlock={handleRemoveBlock}
            onChangeFixedSlot={handleChangeFixedSlot}
            mode={mode}
            device={device}
            readOnly={readOnly}
          />
          <EmailBuilderProperties
            draft={draft}
            selectedBlockId={selectedBlockId}
            readOnly={readOnly}
            onChangeRef={handleChangeRef}
            onChangeImagePresentation={handleChangeImagePresentation}
            onAddCtaEcho={handleAddCtaEcho}
            hasCtaEcho={hasCtaEcho}
          />
        </div>
      </DndContext>
    </div>
  );
}
